import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import usersApi from '../../api/users';
import useAuth from '../../hooks/useAuth';
import { ROLE_LABELS } from '../../utils/roles';

// ── Shared primitives ─────────────────────────────────────────────────────────

const GENDER_LABELS = { M: 'Male', F: 'Female', O: 'Other', N: 'Prefer not to say' };

const ROLE_BADGE_COLORS = {
  UPPER_ADMIN: 'bg-indigo-100 text-indigo-700',
  ADMIN:       'bg-blue-100 text-blue-700',
  SSL:         'bg-yellow-100 text-yellow-700',
  SL:          'bg-gray-100 text-gray-600',
};

function Badge({ color, children }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>
      {children}
    </span>
  );
}

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400';

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

// ── User form modal (create + edit) ───────────────────────────────────────────

const BLANK_CREATE = {
  email: '', username: '', first_name: '', last_name: '',
  role: 'SL', gender: '', phone: '', district: '', password: '',
};

// Extract a human-readable message from a DRF error (field errors or detail)
function drf_error_message(err) {
  if (!err) return null;
  const data = err.data;
  if (!data) return err.message || 'An error occurred.';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  // Field-level errors: { field: ["msg1", ...], non_field_errors: [...] }
  const parts = Object.entries(data).flatMap(([field, msgs]) => {
    const label = field === 'non_field_errors' ? '' : `${field}: `;
    return (Array.isArray(msgs) ? msgs : [msgs]).map(m => `${label}${m}`);
  });
  return parts.join(' ') || err.message || 'An error occurred.';
}

function UserFormModal({ initial, districts, isUpperAdmin, currentDistrict, isPending, error, onClose, onSave }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    isEdit
      ? { role: initial.role, gender: initial.gender ?? '', phone: initial.phone ?? '', district: initial.district ?? '' }
      : { ...BLANK_CREATE, district: currentDistrict ?? '' }
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    // For non-upper-admins creating a user, district is always their own
    if (!isUpperAdmin) data.district = currentDistrict;
    // Convert empty strings to null for FK/optional fields so DRF doesn't reject them
    if (data.district === '' || data.district === undefined) data.district = null;
    if (data.gender === '') data.gender = null;
    if (data.phone === '') data.phone = null;
    onSave(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-gray-900">{isEdit ? 'Edit User' : 'Add User'}</p>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
            {!isEdit && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name" required>
                    <input className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
                  </Field>
                  <Field label="Last Name" required>
                    <input className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} required />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} required />
                </Field>
                <Field label="Username" required>
                  <input className={inputCls} value={form.username}
                         onChange={e => set('username', e.target.value)}
                         placeholder="Tip: use first.last or VT PID"
                         required />
                </Field>
                <Field label="Temporary Password" required>
                  <input type="password" className={inputCls} value={form.password}
                         onChange={e => set('password', e.target.value)} required />
                </Field>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Role" required>
                <select className={inputCls} value={form.role} onChange={e => set('role', e.target.value)}>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Gender">
                <select className={inputCls} value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(GENDER_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Phone">
              <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(540) 555-0100" />
            </Field>

            {isUpperAdmin && (
              <Field label="District">
                <select className={inputCls} value={form.district}
                        onChange={e => set('district', e.target.value)}
                        disabled={form.role === 'UPPER_ADMIN'}>
                  <option value="">— None (Upper Admin) —</option>
                  {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
            )}
          </div>

          {error && (
            <div className="mx-5 mb-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {drf_error_message(error)}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
            <button type="button" onClick={onClose}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={isPending}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
              {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reset password modal ───────────────────────────────────────────────────────

function ResetPasswordModal({ user, isPending, onClose, onReset }) {
  const [pw, setPw] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-gray-900">Reset Password</p>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">✕</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            Set a new password for <strong>{user.first_name} {user.last_name}</strong>.
            They should change it on first login.
          </p>
          <Field label="New Password" required>
            <input type="password" className={inputCls} value={pw}
                   onChange={e => setPw(e.target.value)} autoFocus />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => pw && onReset(pw)} disabled={!pw || isPending}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
            {isPending ? 'Resetting…' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

const ROLE_FILTER_OPTIONS = [
  { key: '',            label: 'All' },
  { key: 'SL',          label: 'SL' },
  { key: 'SSL',         label: 'SSL' },
  { key: 'ADMIN',       label: 'Admin' },
  { key: 'UPPER_ADMIN', label: 'Upper Admin' },
];

export default function UsersPanel() {
  const { user: me }  = useAuth();
  const isUpperAdmin  = me?.role === 'UPPER_ADMIN';
  const queryClient   = useQueryClient();

  const [roleFilter,   setRoleFilter]   = useState('');
  const [showCreate,   setShowCreate]   = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [resettingPw,  setResettingPw]  = useState(null);
  const [toast,        setToast]        = useState(null);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  usersApi.getAll,
  });

  const { data: districts = [] } = useQuery({
    queryKey: ['districts'],
    queryFn:  usersApi.getDistricts,
    enabled:  isUpperAdmin,
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess:  () => { refetch(); setShowCreate(false); flash('User created.'); },
    onError:    (err) => flash(err.message ?? 'Failed to create user.', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => usersApi.update(id, data),
    onSuccess:  () => { refetch(); setEditing(null); flash('User updated.'); },
    onError:    (err) => flash(err.message ?? 'Failed to update user.', 'error'),
  });

  const deactivateMutation = useMutation({
    mutationFn: usersApi.deactivate,
    onSuccess:  () => { refetch(); flash('User deactivated.'); },
    onError:    (err) => flash(err.message, 'error'),
  });

  const activateMutation = useMutation({
    mutationFn: usersApi.activate,
    onSuccess:  () => { refetch(); flash('User activated.'); },
    onError:    (err) => flash(err.message, 'error'),
  });

  const resetPwMutation = useMutation({
    mutationFn: ({ id, pw }) => usersApi.resetPassword(id, { new_password: pw }),
    onSuccess:  () => { setResettingPw(null); flash('Password reset.'); },
    onError:    (err) => flash(err.message, 'error'),
  });

  const filtered = roleFilter ? users.filter(u => u.role === roleFilter) : users;

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 flex-wrap">
          {ROLE_FILTER_OPTIONS.map(({ key, label }) => (
            <button key={key} onClick={() => setRoleFilter(key)}
                    className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                      roleFilter === key
                        ? 'bg-indigo-600 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
          + Add User
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <tr>
                {['Name', 'Email', 'Role', 'Gender', 'Phone', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No users found.</td>
                </tr>
              )}
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                    {u.first_name} {u.last_name}
                    {u.id === me?.id && <span className="ml-1.5 text-xs text-gray-400">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge color={ROLE_BADGE_COLORS[u.role] ?? 'bg-gray-100 text-gray-600'}>
                      {ROLE_LABELS[u.role] ?? u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{GENDER_LABELS[u.gender] ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{u.phone || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge color={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setEditing(u)}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                        Edit
                      </button>
                      <button onClick={() => setResettingPw(u)}
                              className="text-xs font-medium text-gray-500 hover:text-gray-800">
                        Reset PW
                      </button>
                      {u.id !== me?.id && (
                        u.is_active
                          ? (
                            <button onClick={() => deactivateMutation.mutate(u.id)}
                                    className="text-xs font-medium text-red-500 hover:text-red-700">
                              Deactivate
                            </button>
                          ) : (
                            <button onClick={() => activateMutation.mutate(u.id)}
                                    className="text-xs font-medium text-green-600 hover:text-green-800">
                              Activate
                            </button>
                          )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <UserFormModal
          districts={districts}
          isUpperAdmin={isUpperAdmin}
          currentDistrict={me?.district}
          isPending={createMutation.isPending}
          error={createMutation.error}
          onClose={() => setShowCreate(false)}
          onSave={(data) => createMutation.mutate(data)}
        />
      )}
      {editing && (
        <UserFormModal
          initial={editing}
          districts={districts}
          isUpperAdmin={isUpperAdmin}
          currentDistrict={me?.district}
          isPending={updateMutation.isPending}
          error={updateMutation.error}
          onClose={() => setEditing(null)}
          onSave={(data) => updateMutation.mutate({ id: editing.id, data })}
        />
      )}
      {resettingPw && (
        <ResetPasswordModal
          user={resettingPw}
          isPending={resetPwMutation.isPending}
          onClose={() => setResettingPw(null)}
          onReset={(pw) => resetPwMutation.mutate({ id: resettingPw.id, pw })}
        />
      )}
    </div>
  );
}
